import { Component } from '@angular/core';
import { BlockBereavementResultsComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/bereavement/block-bereavement-results.component';
import { BlockBondingResultsComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/bonding/block-bonding-results.component';
import { BlockFmlaOnlyResultsComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/fmla-only/block-fmla-only-results.component';
import { BlockGivingBirthResultsComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/giving-birth/block-giving-birth-results.component';
import { BlockSickOrInjuredResultsComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/sick-or-injured/block-sick-or-injured-results.component';
import { BlockSurgeryResultsComponent } from '@core-app/blocks/block-leave-of-absence-tool/results/surgery/block-surgery-results.component';
import { AuthService, BlockAbstractComponent, CacheService, ParserService } from '@gallaghercp/core-frontend';
import { TranslatePipe } from '@gallaghercp/core-frontend-contracts';
import { ClientService } from '../../services/client.service';

@Component({
    selector: 'core-leave-of-absence-tool',
    imports: [
        BlockGivingBirthResultsComponent,
        BlockSickOrInjuredResultsComponent,
        BlockSurgeryResultsComponent,
        BlockBondingResultsComponent,
        BlockFmlaOnlyResultsComponent,
        BlockBereavementResultsComponent,
        TranslatePipe,
    ],
    templateUrl: './block-leave-of-absence-tool.component.html',
    styleUrls: [
        './scss/block-leave-of-absence-tool-charts.scss',
        './scss/block-leave-of-absence-tool-questions.scss',
        './scss/block-leave-of-absence-tool-timeline.scss',
    ],
})
export class BlockLeaveOfAbsenceToolComponent extends BlockAbstractComponent {
    quizIsComplete = false;
    selectedRadioOptionString = ''; // String for handling radio choice. This is reset after each question
    answersArray: Record<string, any> = {}; // Selected answers logic
    quizHasStarted = false; //  Start and restart logic
    chosenReason = '';
    timelineChartData = this.block?.data.timelineChartData;
    resultsAnswersKey = '';
    birthTimelineChartKey = '';
    sicknessOrInjuryTimelineChartKey = '';
    surgeryTimelineChartKey = '';
    bondingTimelineChartKey = '';

    enableLeaveOfAbsenceTool: boolean =
        (this.configService.get('enableLeaveOfAbsenceTool') as boolean | undefined) ?? false;

    constructor(
        protected override parser: ParserService,
        protected override auth: AuthService,
        protected client: ClientService,
        protected cache: CacheService,
    ) {
        super(parser, auth);
    }

    /**
     * Returns a translated question object for the given data key.
     * Logic fields (questionName, answer values) always come from the English default
     * so switch statements and answer comparisons continue to work. Display fields
     * (questionText, answer descriptions, breadcrumbValues) come from the translation.
     */
    private getTranslatedQuestion(key: string): any {
        const langId = this.getCurrentLanguageId();
        const translated = this.block?.languageData?.[langId]?.[key];
        const english = this.block?.languageData?.[1]?.[key] ?? this.block?.data?.[key];

        if (!translated || langId === 1) {
            return english;
        }

        // Answers array may be stored under any key name — find it by type
        const translatedAnswers: any[] = Object.values(translated).find(Array.isArray) ?? [];

        return {
            ...translated,
            questionName: english?.questionName,
            answers:
                english?.answers?.map((engAnswer: any, i: number) => {
                    // Positional match — order is preserved but the value field may have been
                    // renamed and/or translated, so we cannot rely on a.value === engAnswer.value
                    const ta = translatedAnswers[i];
                    // Exclude 'value' if the key survived unchanged; take first string as
                    // description and last as breadcrumbValue so a translated value field
                    // sitting between them does not corrupt the mapping
                    const strings = Object.entries(ta ?? {})
                        .filter(([k, v]) => k !== 'value' && typeof v === 'string')
                        .map(([, v]) => v as string);
                    return {
                        ...engAnswer,
                        description: strings[0] ?? engAnswer.description,
                        breadcrumbValue: strings[strings.length - 1] ?? engAnswer.breadcrumbValue,
                    };
                }) ?? translatedAnswers,
        };
    }

    get leaveReasonQuestion() {
        return this.getTranslatedQuestion('leaveReasonQuestion');
    }

    // Question sets, which determine which questions are shown
    get birthQuestionSet() {
        if (this.answersArray['employedDuration'] === 'ninetyDaysOrLess') {
            return [this.getTranslatedQuestion('howLongQuestionBirth')];
        }
        return [
            this.getTranslatedQuestion('howLongQuestionBirth'),
            this.getTranslatedQuestion('hourlyOrSalariedQuestion'),
            this.getTranslatedQuestion('deliveryTypeQuestion'),
        ];
    }

    get sicknessOrInjuryQuestionSet() {
        if (this.answersArray['hourlyOrSalaried'] === 'salaried') {
            return [
                this.getTranslatedQuestion('howLongQuestionGeneric'),
                this.getTranslatedQuestion('hourlyOrSalariedQuestion'),
                this.getTranslatedQuestion('timeAwayFromWorkWithRecurringQuestionThree'),
            ];
        }
        if (this.answersArray['employedDuration'] === 'ninetyDaysOrLess') {
            return [this.getTranslatedQuestion('howLongQuestionGeneric')];
        }
        return [
            this.getTranslatedQuestion('howLongQuestionGeneric'),
            this.getTranslatedQuestion('hourlyOrSalariedQuestion'),
            this.getTranslatedQuestion('timeAwayFromWorkWithRecurringQuestionSeven'),
        ];
    }

    get surgeryQuestionSet() {
        if (this.answersArray['hourlyOrSalaried'] === 'salaried') {
            return [
                this.getTranslatedQuestion('howLongQuestionGeneric'),
                this.getTranslatedQuestion('hourlyOrSalariedQuestion'),
                this.getTranslatedQuestion('timeAwayFromWorkQuestionThree'),
            ];
        }
        if (this.answersArray['employedDuration'] === 'ninetyDaysOrLess') {
            return [this.getTranslatedQuestion('howLongQuestionGeneric')];
        }
        return [
            this.getTranslatedQuestion('howLongQuestionGeneric'),
            this.getTranslatedQuestion('hourlyOrSalariedQuestion'),
            this.getTranslatedQuestion('timeAwayFromWorkQuestionSeven'),
        ];
    }

    get bondingOnlyQuestionSet() {
        return [this.getTranslatedQuestion('howLongQuestionBonding')];
    }

    get fmlaOnlyQuestionSet() {
        return [this.getTranslatedQuestion('howLongQuestionGeneric')];
    }

    get bereavementQuestionSet() {
        return [this.getTranslatedQuestion('whoHasPassedAwayQuestion')];
    }

    get selectedRadioOption() {
        return this.selectedRadioOptionString;
    }

    set selectedRadioOption(value: string) {
        this.selectedRadioOptionString = value;
    }

    get selectedAnswers() {
        return this.answersArray;
    }

    get answersArrayLength() {
        return Object.keys(this.answersArray).length;
    }

    get breadcrumbValues(): string[] {
        const values: string[] = [];

        if (this.chosenReason) {
            const reasonAnswer = this.leaveReasonQuestion?.answers?.find((a: any) => a.value === this.chosenReason);
            if (reasonAnswer?.breadcrumbValue) {
                values.push(reasonAnswer.breadcrumbValue);
            }
        }

        if (this.questionsForChosenReason) {
            for (const [questionName, answerValue] of Object.entries(this.answersArray)) {
                const question = this.questionsForChosenReason.find((q: any) => q.questionName === questionName);
                const answer = question?.answers?.find((a: any) => a.value === answerValue);
                if (answer?.breadcrumbValue) {
                    values.push(answer.breadcrumbValue);
                }
            }
        }

        return values;
    }

    addSelectedOption(questionName: string, newItem: string) {
        this.answersArray[questionName] = newItem;
        this.selectedRadioOption = '';

        if (this.answersArrayLength === this.questionsForChosenReason!.length && this.quizHasStarted) {
            this.quizIsComplete = true;
            this.createResultsAnswersKey();
        }
    }

    createResultsAnswersKey() {
        const reason = this.chosenReason;
        const answers = this.selectedAnswers;

        // Start with the reason value
        let key = reason;

        // Iterate through the selectedAnswers object
        Object.values(answers).forEach(value => {
            // Capitalize the first letter of each answer value
            const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
            // Concatenate to the key
            key += capitalizedValue;
        });

        this.resultsAnswersKey = key;

        // Call getTimelineChartRef to set the appropriate timeline chart key
        this.getTimelineChartRef();
    }

    // Mapping objects for each leave type
    private birthChartMapping: Record<string, string> = {
        birthTwelveMonthsOrMoreHourlyNatural: 'oneBirthNaturalWithFmlaHourly',
        birthTwelveMonthsOrMoreHourlyCesarean: 'twoBirthCesareanWithFmlaHourly',
        birthTwelveMonthsOrMoreSalariedNatural: 'threeBirthNaturalWithFmlaSalary',
        birthTwelveMonthsOrMoreSalariedCesarean: 'fourBirthCesareanWithFmlaSalary',
        birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyNatural: 'fiveBirthNaturalNoFmlaHourly',
        birthLessThanTwelveMonthsButMoreThanNinetyDaysHourlyCesarean: 'sixBirthCesareanNoFmlaHourly',
        birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedNatural: 'sevenBirthNaturalNoFmlaSalary',
        birthLessThanTwelveMonthsButMoreThanNinetyDaysSalariedCesarean: 'eightBirthCesareanNoFmlaSalary',
    };

    private sicknessOrInjuryChartMapping: Record<string, string> = {
        sicknessOrInjuryTwelveMonthsOrMoreHourlyLong: 'twoSickWithFmlaHourlyMoreThanSeven',
        sicknessOrInjuryTwelveMonthsOrMoreSalariedLong: 'fiveSickWithFmlaSalaryMoreThanThreeDays',
        sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong: 'eightSickNoFmlaHourlyMoreThanSevenDays',
        sicknessOrInjuryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong:
            'elevenSickNoFmlaSalaryMoreThanThreeDays',
    };

    private surgeryChartMapping: Record<string, string> = {
        surgeryTwelveMonthsOrMoreHourlyLong: 'twoSurgeryWithFmlaHourlyMoreThanSevenDays',
        surgeryTwelveMonthsOrMoreSalariedLong: 'twoSurgeryWithFmlaSalaryMoreThanThreeDays',
        surgeryLessThanTwelveMonthsButMoreThanNinetyDaysHourlyLong: 'sixSurgeryNoFmlaHourlyMoreThanSevenDays',
        surgeryLessThanTwelveMonthsButMoreThanNinetyDaysSalariedLong: 'eightSurgeryNoFmlaSalaryMoreThanThreeDays',
    };

    private bondingChartMapping: Record<string, string> = {
        bondingOnlyTwelveMonthsOrMore: 'oneBondingOverTwelve',
        bondingOnlyLessThanTwelveMonthsButMoreThanNinetyDays: 'twoBondingLessThanTwelve',
    };

    getTimelineChartRef() {
        // Reset all timeline chart keys
        this.birthTimelineChartKey = '';
        this.sicknessOrInjuryTimelineChartKey = '';
        this.surgeryTimelineChartKey = '';
        this.bondingTimelineChartKey = '';

        // Check if the resultsAnswersKey starts with a specific leave type and set the appropriate timeline chart key
        if (this.resultsAnswersKey.startsWith('birth')) {
            this.birthTimelineChartKey = this.birthChartMapping[this.resultsAnswersKey] || '';
        } else if (this.resultsAnswersKey.startsWith('sicknessOrInjury')) {
            this.sicknessOrInjuryTimelineChartKey = this.sicknessOrInjuryChartMapping[this.resultsAnswersKey] || '';
        } else if (this.resultsAnswersKey.startsWith('surgery')) {
            this.surgeryTimelineChartKey = this.surgeryChartMapping[this.resultsAnswersKey] || '';
        } else if (this.resultsAnswersKey.startsWith('bondingOnly')) {
            this.bondingTimelineChartKey = this.bondingChartMapping[this.resultsAnswersKey] || '';
        }
    }

    removeLastSelectedOption() {
        if (this.answersArrayLength === 0) {
            this.chosenReason = '';
        }
        delete this.answersArray[Object.keys(this.answersArray)[Object.keys(this.answersArray).length - 1]];
        this.selectedRadioOption = '';
    }

    // Chosen reason. Text value is set using leaveReasonQuestion.value
    // Then, questionsForChosenReason uses this to look up leaveReasonQuestion.questions for the correct question set.
    chooseLeaveReason(chosenReason: string) {
        this.chosenReason = chosenReason;
        this.selectedRadioOption = '';
        this.answersArray = {};
    }

    get questionsForChosenReason() {
        switch (this.chosenReason) {
            case 'birth':
                return this.birthQuestionSet;
            case 'sicknessOrInjury':
                return this.sicknessOrInjuryQuestionSet;
            case 'surgery':
                return this.surgeryQuestionSet;
            case 'bondingOnly':
                return this.bondingOnlyQuestionSet;
            case 'fmlaOnly':
                return this.fmlaOnlyQuestionSet;
            case 'bereavement':
                return this.bereavementQuestionSet;
            default:
                return undefined;
        }
    }

    startQuiz() {
        this.answersArray = {};
        this.chosenReason = '';
        this.selectedRadioOption = '';
        this.quizHasStarted = true;
        this.quizIsComplete = false;
    }

    startOver() {
        this.quizHasStarted = false;
        this.quizIsComplete = false;
        this.answersArray = {};
        this.chosenReason = '';
        this.selectedRadioOption = '';
    }
}
